/**
 * Team views: the hub's 'views' module (GET/POST/DELETE /api/ext/views). In demo mode they
 * live in memory for the tab, so the whole flow can be shown without a hub.
 */
import type { SharedView, SharedViewInput, ViewsState } from '@orgus/protocol';
import { ApiError, apiDelete, apiGet, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { setTeamViews, useViews } from './store.ts';

let mockViews: SharedView[] = [];
let inflight: Promise<void> | null = null;

const isMock = () => useOrgus.getState().mode === 'mock';

/** Load team views; a hub without the module leaves the team list off. */
export function loadTeamViews(): Promise<void> {
  if (isMock()) {
    setTeamViews(mockViews);
    return Promise.resolve();
  }
  inflight ??= apiGet<ViewsState>('/api/ext/views')
    .then((s) => setTeamViews(s.views))
    .catch((e: unknown) => {
      // 404: the hub has no views module. Anything else: keep what we had.
      if (e instanceof ApiError && e.status === 404) setTeamViews(null);
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export async function shareView(input: SharedViewInput): Promise<SharedView> {
  if (isMock()) {
    const existing = mockViews.find((v) => v.path === input.path && v.name.toLowerCase() === input.name.toLowerCase());
    const view: SharedView = { id: existing?.id ?? `vw_${Date.now().toString(36)}`, ...input, createdBy: 'owner', createdAt: Date.now() };
    mockViews = [...mockViews.filter((v) => v.id !== view.id), view];
    setTeamViews(mockViews);
    return view;
  }
  const view = await apiPost<SharedView>('/api/ext/views', input);
  await loadTeamViews();
  return view;
}

export async function deleteTeamView(id: string): Promise<void> {
  if (isMock()) {
    mockViews = mockViews.filter((v) => v.id !== id);
    setTeamViews(mockViews);
    return;
  }
  await apiDelete(`/api/ext/views/${encodeURIComponent(id)}`);
  await loadTeamViews();
}

export const teamAvailable = (): boolean => useViews.getState().teamAvailable;
